import { useCallback, useState } from 'react'
import { useGenerateImage } from 'recharts-to-png'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useThemeAppearance } from '~/features/layout/hooks'

export interface UseChartExportImageResult {
  ref: React.Ref<HTMLDivElement | null>
  handleExportImage: () => Promise<void>
  isExporting: boolean
}

export function useChartExportImage(filename: string): UseChartExportImageResult {
  const { t } = useTranslation('common')
  const [theme] = useThemeAppearance()
  const [isExporting, setIsExporting] = useState(false)
  const [getDivJpeg, { ref }] = useGenerateImage<HTMLDivElement>({
    options: {
      backgroundColor: theme === 'dark' ? 'black' : 'white',
      // @ts-ignore
      windowWidth: typeof window !== 'undefined' && window.innerWidth >= 1420 ? 1200 : undefined,
      ignoreElements: (element) => element.classList.contains('no-export'),
      scale: 1.2
    }
  })

  const handleExportImage = useCallback(async () => {
    if (isExporting) return

    setIsExporting(true)

    // Show loading toast
    const loadingToast = toast.loading(t('exportingChart'), {
      description: t('exportChartDescription')
    })

    try {
      await new Promise<void>((resolve) => {
        setTimeout(async () => {
          try {
            await getDivJpeg((blob: Blob | null) => {
              if (!blob) {
                toast.error(t('exportChartFailed'), { id: loadingToast, description: '' })
                return resolve()
              }

              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = filename
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
              URL.revokeObjectURL(url)

              toast.success(t('exportChartSuccess'), { id: loadingToast, description: '' })
              resolve()
            })
          } catch (error) {
            console.error('Export error:', error)
            toast.error(t('exportChartFailed'), { id: loadingToast, description: '' })
            resolve()
          }
        }, 10)
      })
    } finally {
      setIsExporting(false)
    }
  }, [getDivJpeg, filename, isExporting])

  return { ref, handleExportImage, isExporting }
}
